import { Button, Divider, Form, Input, message, Typography } from "antd"
import React, { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { Tarif595AttendeesEndUserAppQuery, useTarif595AttendeesEndUserAppQuery, useUpdateTarif595AttendeeEndUserAppMutation } from "../../generated/graphql"
import { getGraphQLErrorMessage } from "../../helpers/sentry"
import css from "./AccountAttendees.module.css"

type Attendee = Tarif595AttendeesEndUserAppQuery["tarif595AttendeesEndUserApp"][number]
type FormValues = { ahvNumber?: string; healthInsurer?: string; healthInsuranceNumber?: string }

const fullName = (attendee: Attendee) => [attendee.firstname, attendee.lastname].filter(Boolean).join(" ")

const AttendeeForm: FC<{ attendee: Attendee; onSaved: () => void; onCancel: () => void }> = ({ attendee, onSaved, onCancel }) => {
    const { t } = useTranslation()
    const [update, { loading }] = useUpdateTarif595AttendeeEndUserAppMutation({ fetchPolicy: "no-cache" })

    const save = async ({ ahvNumber, healthInsurer, healthInsuranceNumber }: FormValues) => {
        try {
            await update({
                variables: {
                    attendeeId: attendee.id,
                    // Leere AHV-Nummer heißt: die gespeicherte bleibt. Das Feld zeigt sie nie im Klartext.
                    input: { healthInsurer: healthInsurer ?? "", healthInsuranceNumber: healthInsuranceNumber ?? "", ...(ahvNumber?.trim() ? { ahvNumber } : {}) },
                },
            })
            message.success(t("account.tarif595.saved", "Gespeichert."))
            onSaved()
        } catch (error) {
            const raw = getGraphQLErrorMessage(error) || ""
            // Nur die Prüfmeldungen des Servers durchreichen, keine technischen Fehler
            message.error(/AHV-Nummer|zu lang/.test(raw) ? raw : t("account.tarif595.saveError", "Die Angaben konnten nicht gespeichert werden."))
        }
    }

    return (
        <Form<FormValues>
            layout='vertical'
            className={css.row}
            style={{ display: "block" }}
            data-testid='account-tarif595-form'
            initialValues={{ healthInsurer: attendee.healthInsurer ?? "", healthInsuranceNumber: attendee.healthInsuranceNumber ?? "" }}
            onFinish={save}
        >
            <div className={css.name}>{fullName(attendee)}</div>
            <Form.Item
                name='ahvNumber'
                label={t("account.tarif595.ahvLabel", "AHV-Nummer")}
                extra={attendee.ahvNumberMasked ? t("account.tarif595.ahvStored", { masked: attendee.ahvNumberMasked, defaultValue: "Gespeichert: {{masked}}. Nur ausfüllen, wenn sie sich geändert hat." }) : undefined}
            >
                <Input placeholder='756.1234.5678.97' autoComplete='off' inputMode='numeric' maxLength={20} data-testid='account-tarif595-ahv' />
            </Form.Item>
            <Form.Item name='healthInsurer' label={t("account.tarif595.insurerLabel", "Krankenkasse")}>
                <Input maxLength={100} data-testid='account-tarif595-insurer' />
            </Form.Item>
            <Form.Item name='healthInsuranceNumber' label={t("account.tarif595.insuranceNumberLabel", "Versichertennummer")}>
                <Input maxLength={100} data-testid='account-tarif595-insurance-number' />
            </Form.Item>
            <Button type='primary' htmlType='submit' loading={loading} data-testid='account-tarif595-save'>
                {t("account.tarif595.save", "Speichern")}
            </Button>{" "}
            <Button onClick={onCancel}>{t("account.attendees.cancel", "Abbrechen")}</Button>
        </Form>
    )
}

/** Tarif 595: Angaben je Teilnehmer für den Krankenkassenbeleg. Sichtbar nur bei tarif595Enabled, siehe AccountAttendees. */
const Tarif595AttendeeData: FC = () => {
    const { t } = useTranslation()
    const [editingId, setEditingId] = useState<string>()
    // Nicht im Apollo-Cache halten: Der Logout leert ihn nicht, und die Abfrage hat kein Argument. Auf einem geteilten
    // Gerät sähe die nächste Person sonst kurz die Kinder und Versichertendaten der vorherigen.
    const { data, refetch } = useTarif595AttendeesEndUserAppQuery({ fetchPolicy: "no-cache" })
    const attendees = data?.tarif595AttendeesEndUserApp ?? []

    if (attendees.length === 0) return null

    return (
        <section data-testid='account-tarif595-attendees'>
            <Divider />
            <Typography.Title level={5}>{t("account.tarif595.title", "Angaben für den Krankenkassenbeleg")}</Typography.Title>
            <Typography.Paragraph type='secondary'>
                {t("account.tarif595.intro", "Damit stellt die Schule den Rückforderungsbeleg für die Zusatzversicherung aus. Die AHV-Nummer wird verschlüsselt gespeichert und steht auf dem Beleg.")}
            </Typography.Paragraph>
            <div className={css.list}>
                {attendees.map((attendee) =>
                    editingId === attendee.id ? (
                        <AttendeeForm
                            key={attendee.id}
                            attendee={attendee}
                            onCancel={() => setEditingId(undefined)}
                            onSaved={() => {
                                setEditingId(undefined)
                                refetch()
                            }}
                        />
                    ) : (
                        <div key={attendee.id} className={css.row} data-testid='account-tarif595-row'>
                            <span className={css.body}>
                                <span className={css.name}>{fullName(attendee)}</span>
                                <span className={css.meta}>{[attendee.ahvNumberMasked ?? t("account.tarif595.ahvMissing", "AHV-Nummer fehlt"), attendee.healthInsurer].filter(Boolean).join(" · ")}</span>
                            </span>
                            <span className={css.actions}>
                                <Button size='small' type='link' onClick={() => setEditingId(attendee.id)} data-testid='account-tarif595-edit'>
                                    {t("account.attendees.edit", "Bearbeiten")}
                                </Button>
                            </span>
                        </div>
                    ),
                )}
            </div>
        </section>
    )
}

export default Tarif595AttendeeData
